"use client";

/* eslint-disable react/no-unknown-property */
import { type DialogProps } from "@radix-ui/react-dialog";
import { Command as CommandPrimitive } from "cmdk";
import { Dialog, DialogContent } from "components/ui/Dialog";
import classNames from "components/ui/classNames";
import { SearchIcon } from "lucide-react";
import * as React from "react";

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={classNames("flex h-full w-full flex-col overflow-hidden rounded-md", className)}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0">
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div className="flex px-1 pb-2" cmdk-input-wrapper="">
    <CommandPrimitive.Input
      ref={ref}
      className={classNames(
        // "h-12 w-full rounded-xl border-transparent bg-zinc-100 text-base placeholder:text-zinc-400",
        // "font-bold placeholder:font-medium",
        // "focus:border-transparent focus:bg-zinc-50 focus:ring-[1.5px] focus:ring-inset focus:ring-zinc-400 focus:ring-offset-transparent",
        // "md:text-sm",

        "w-full",
        "h-10 rounded-xl border-gray-300 text-base dark:border-slate-700 dark:bg-slate-950",
        "font-medium placeholder:font-medium dark:font-bold dark:placeholder:font-bold dark:placeholder:text-zinc-300/50",
        "focus:border-transparent focus:bg-blue-50 focus:ring-[1.5px] focus:ring-inset focus:ring-blue-400 focus:ring-offset-transparent",
        "md:text-sm",
        "dark:focus:bg-blue-400/10 dark:focus-visible:outline-transparent",

        className,
      )}
      {...props}
    />
  </div>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandInputOriginal = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div className="flex items-center border-b px-3 dark:border-slate-600/40" cmdk-input-wrapper="">
    <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      className={classNames(
        "flex h-10 w-full rounded-md border-none bg-transparent py-3 text-sm outline-none",
        "focus:ring-0 focus-visible:outline-none",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  </div>
));

CommandInputOriginal.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={classNames("max-h-64 overflow-y-auto overflow-x-hidden", className)}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => <CommandPrimitive.Empty ref={ref} className="py-6 text-center text-sm" {...props} />);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={classNames(
      "overflow-hidden py-1.5 text-slate-800 [&_[cmdk-group-items]]:space-y-1",
      "[&_[cmdk-group-heading]]:text-muted-slate-800 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium",
      className,
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator ref={ref} className={classNames("-mx-1 h-px bg-slate-200", className)} {...props} />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={classNames(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",

      "hover:bg-viridian-400/10 hover:text-viridian-600 data-[selected=true]:hover:bg-viridian-50",
      "dark:hover:bg-blue-400/20 dark:hover:text-blue-200",

      "data-[active=true]:bg-blue-400/10 data-[active=true]:text-blue-600",
      "data-[active=true]:hover:bg-rose-50 data-[active=true]:hover:text-rose-600",
      "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",

      "dark:data-[active=true]:bg-blue-400/20 dark:data-[active=true]:text-blue-200",
      "dark:font-bold dark:text-gray-300/80",

      "[&_svg]:pointer-events-none [&_svg]:shrink-0",
      "[&_svg]:size-4",
      className,
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return <span className={classNames("text-muted-foreground ml-auto text-xs tracking-widest", className)} {...props} />;
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandInputOriginal,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
